import type { BottomNavKey, ChallengeProps, GameRowProps } from '@words-happen/ui-components';
import { BottomNav, Challenge, GameRow, ModeCard, PriorityCard, SectionLabel } from '@words-happen/ui-components';
import { Avatar } from '@words-happen/ui-components/identity';
import type React from 'react';
import styles from './dashboard-screen.module.scss';

export interface DashboardScreenWho {
  name: string;
  avatarColor?: string;
  avatarSrc?: string;
}

export interface DashboardScreenPriority {
  kind: 'your-turn' | 'daily-streak';
  title: string;
  subtitle?: string;
  badge?: { text: string; tone: 'urgent' | 'reward' };
  opponent?: { name: string; color: string };
  streak?: number;
  onSelect: () => void;
}

export interface DashboardScreenGame {
  id: string;
  opponent: GameRowProps['opponent'];
  status: GameRowProps['status'];
  score?: number;
  lastMove?: string;
  onSelect?: () => void;
}

export interface DashboardScreenMode {
  id: string;
  color: string;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  onSelect: () => void;
}

export interface DashboardScreenChallenge {
  id: string;
  title: string;
  subtitle?: string;
  reward: string;
  progress: number;
  total: number;
  badge?: ChallengeProps['badge'];
  done?: boolean;
  icon: React.ReactNode;
}

export interface DashboardScreenNav {
  active: BottomNavKey;
  onNav: (key: BottomNavKey) => void;
  badges?: Partial<Record<BottomNavKey, number>>;
  disabledTabs?: BottomNavKey[];
}

/** Where the header's avatar and greeting lead: the player's profile. */
export interface DashboardScreenProfileLink {
  href: string;
  /** Called for a plain click, in place of following `href`, so the app can route without a page load. */
  onOpen: () => void;
}

export interface DashboardScreenProps {
  who: DashboardScreenWho;
  /** Makes the header's avatar and greeting a link, named "Your profile". */
  profileLink?: DashboardScreenProfileLink;
  priority?: DashboardScreenPriority;
  games: DashboardScreenGame[];
  modes: DashboardScreenMode[];
  challenges: DashboardScreenChallenge[];
  nav: DashboardScreenNav;
  className?: string;
}

/** A click the browser would handle itself: a new tab or window, a download, or not the main button. */
const isModifiedClick = (event: React.MouseEvent) =>
  event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

const Greeting: React.FC<{ who: DashboardScreenWho; profileLink?: DashboardScreenProfileLink }> = ({
  who,
  profileLink,
}) => {
  const content = (
    <>
      <Avatar name={who.name} color={who.avatarColor} src={who.avatarSrc} size={48} />
      <span className={styles.greeting}>Hi, {who.name}</span>
    </>
  );

  if (!profileLink) {
    return content;
  }

  return (
    <a
      className={styles.profileLink}
      href={profileLink.href}
      aria-label="Your profile"
      onClick={(event) => {
        if (!isModifiedClick(event)) {
          event.preventDefault();
          profileLink.onOpen();
        }
      }}
    >
      {content}
    </a>
  );
};

export const DashboardScreen: React.FC<DashboardScreenProps> = ({
  who,
  profileLink,
  priority,
  games,
  modes,
  challenges,
  nav,
  className,
}) => {
  const cls = [styles.screen, className].filter(Boolean).join(' ');

  return (
    <div className={cls}>
      <div className={styles.content}>
        <header className={styles.header}>
          <Greeting who={who} profileLink={profileLink} />
        </header>

        {priority && (
          <section className={styles.section}>
            <PriorityCard
              kind={priority.kind}
              title={priority.title}
              subtitle={priority.subtitle}
              badge={priority.badge}
              opponent={priority.opponent}
              streak={priority.streak}
              onClick={priority.onSelect}
            />
          </section>
        )}

        {games.length > 0 && (
          <section className={styles.section}>
            <SectionLabel>Your games</SectionLabel>
            <ul className={styles.gameList}>
              {games.map((game) => (
                <GameRow
                  key={game.id}
                  opponent={game.opponent}
                  status={game.status}
                  score={game.score}
                  lastMove={game.lastMove}
                  onClick={game.onSelect}
                />
              ))}
            </ul>
          </section>
        )}

        {modes.length > 0 && (
          <section className={styles.section}>
            <SectionLabel>Play a mode</SectionLabel>
            <div className={styles.modeGrid}>
              {modes.map((mode) => (
                <ModeCard
                  key={mode.id}
                  color={mode.color}
                  title={mode.title}
                  subtitle={mode.subtitle}
                  icon={mode.icon}
                  onClick={mode.onSelect}
                />
              ))}
            </div>
          </section>
        )}

        {challenges.length > 0 && (
          <section className={styles.section}>
            <SectionLabel>Challenges</SectionLabel>
            <div className={styles.challengeList}>
              {challenges.map((challenge) => (
                <Challenge
                  key={challenge.id}
                  title={challenge.title}
                  subtitle={challenge.subtitle}
                  reward={challenge.reward}
                  progress={challenge.progress}
                  total={challenge.total}
                  badge={challenge.badge}
                  done={challenge.done}
                  icon={challenge.icon}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      <BottomNav
        className={styles.nav}
        active={nav.active}
        onNav={nav.onNav}
        badges={nav.badges}
        disabledTabs={nav.disabledTabs}
      />
    </div>
  );
};
